<template>
  <div class="a-restaurant-sales-resume">
    <!-- Sección de KPIs de ayer (solo visible en modo mes actual) -->
    <a-card v-if="sales_period === 'current_month'" class="mb-4 pa-3" no_title>
      <v-row align="center">
        <v-col cols="2" md="1" class="text-center grey--text text--lighten-1">
          <div class="text-uppercase font-weight-medium">
            {{ $t("yesterday") }}
          </div>
          <div class="text-caption">
            {{ yesterdayDate }}
          </div>
          <v-icon color="grey lighten-2" size="40"
            >mdi-calendar-month-outline</v-icon
          >
        </v-col>
        <v-col cols="10" md="11">
          <v-row>
            <v-col
              v-for="(item, index) in yesterday_items"
              :key="index"
              cols="12"
              sm="3"
              class="text-center"
            >
              <a-label class="mb-1">
                {{ $t(item.label) }}
              </a-label>
              <a-kpi
                v-if="getValue(item.value, 'report')"
                :kpi="getKpi(item.value)"
                :label="$t(item.label)"
                :value="getValue(item.value, 'report')"
                :vs_values="[getValue(item.value, 'data_to_compare')]"
                :vs_periods="getKpiPeriods"
                large
              ></a-kpi>
              <div v-else>
                -
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </a-card>

    <!-- Título de producción del restaurante -->
    <div
      class="d-flex align-center justify-center mt-6 mb-2 production-title"
      :class="{ 'mt-8': sales_period !== 'current_month' }"
    >
      <div class="d-flex align-center justify-center">
        <v-icon color="grey lighten-1" class="mr-2"
          >mdi-chart-timeline-variant</v-icon
        >
        <h2 class="text-h6 text-uppercase grey--text">
          <span>{{ $t("restaurant_production") }}</span>
        </h2>
      </div>
      <a-period-compare
        :periods="['otb', periodVs]"
        class="ml-2"
      ></a-period-compare>
    </div>

    <!-- Subtítulo con período -->
    <div class="text-center text-subtitle-2 grey--text mb-4">
      {{ periodDateRange }}
    </div>

    <v-row>
      <v-col
        v-for="(item, index) in total_items"
        :key="index"
        cols="12"
        md="3"
        sm="6"
      >
        <a-card-kpi-icon
          :kpi="getKpi(item.value)"
          :label="$t(item.label)"
          :value="getValue(item.value, 'report')"
          :vs_values="[getValue(item.value, 'data_to_compare')]"
          :icon="item.icon"
          :periods="getKpiPeriodsCard"
          hide_periods
          icon_circle
          right
        ></a-card-kpi-icon>
      </v-col>
    </v-row>

    <a-card class="mt-4 mb-8 py-4" no_title>
      <v-row class="grid-forecast">
        <v-col
          v-for="(item, index) in forecast_items[mode]"
          :key="index"
          class="text-center"
        >
          <a-label class="mb-1">
            {{ $t(item.label) }}
          </a-label>
          <a-kpi
            v-if="getValue(item.value, 'report')"
            :kpi="getKpi(item.value)"
            :label="$t(item.label)"
            :value="getValue(item.value, 'report')"
            :vs_values="[getValue(item.value, 'data_to_compare')]"
            :vs_periods="getKpiPeriods"
            large
          ></a-kpi>
          <div v-else>
            -
          </div>
          <template v-if="item.label === 'monthly_projection'">
            <a-kpi
              v-if="getValue('forecast_month_avg.gross', 'report')"
              :kpi="getKpi('forecast_month_avg.gross')"
              :label="$t('forecast_month_avg')"
              :value="getValue('forecast_month_avg.gross', 'report')"
              small
              :clas="'mt-1'"
            ></a-kpi>
          </template>
          <template v-else-if="item.label === 'yearly_projection'">
            <a-kpi
              v-if="getValue('forecast_year_avg.gross', 'report')"
              :kpi="getKpi('forecast_year_avg.gross')"
              :label="$t('forecast_year_avg')"
              :value="getValue('forecast_year_avg.gross', 'report')"
              small
              :clas="'mt-1'"
            ></a-kpi>
          </template>
        </v-col>
      </v-row>
      <div class="d-flex align-center justify-center my-3">
        <div class="d-flex align-center">
          <div class="d-flex align-center mr-4">
            <div
              class="rounded-pill"
              style="width: 20px; height: 3px; background-color: #5489d0;"
            ></div>
            <span
              class="ml-2 caption grey--text text--darken-1 text-capitalize"
            >
              {{ getPeriodLabel() }}
            </span>
          </div>
          <div class="d-flex align-center">
            <div
              class="rounded-pill"
              style="width: 20px; height: 3px; background-color: #edae10;"
            ></div>
            <span
              class="ml-2 caption grey--text text--darken-1 text-capitalize"
            >
              {{ getCompareLabel() }}
            </span>
          </div>
        </div>
      </div>
      <a-restaurant-sales-chart
        :series="chartData"
        :labels="chartLabels"
        height="300"
        class="mt-8 mx-n5 mb-n8"
      ></a-restaurant-sales-chart>
    </a-card>
  </div>
</template>

<script>
import aCard from "@/components/aCard.vue";
import aRestaurantSalesChart from "@/components/chart/aRestaurantSalesChart.vue";
import aCardKpiIcon from "@/components/rms_ui/aCardKpiIcon.vue";
import aKpi from "@/components/rms_ui/aKpi.vue";
import aLabel from "@/components/rms_ui/aLabel.vue";
import aPeriodCompare from "@/components/rms_ui/aPeriodCompare.vue";
import Vue from "vue";
import { mapState } from "vuex";

export default {
  name: "a-restaurant-sales-resume",
  components: {
    aCard,
    aCardKpiIcon,
    aKpi,
    aLabel,
    aRestaurantSalesChart,
    aPeriodCompare
  },
  props: {
    tab_view: {
      type: String,
      default: "resume"
    }
  },
  data: () => ({
    yesterday_items: [
      {
        label: "Income",
        value: "yesterday_total_sale.gross"
      },
      {
        label: "average_ticket_per_diner",
        value: "yesterday_avg_customer_ticket.gross"
      },
      {
        label: "tickets",
        value: "yesterday_total_tickets"
      },
      {
        label: "diners",
        value: "yesterday_total_customers"
      }
    ],
    total_items: [
      {
        icon: "mdi-credit-card-outline",
        label: "Income",
        value: "total_sale.gross"
      },
      {
        icon: "mdi-file-outline",
        label: "income_per_day",
        value: "avg_daily_sale.gross"
      },
      {
        icon: "mdi-account-outline",
        label: "total_diners",
        value: "total_customers"
      },
      {
        icon: "mdi-clock-outline",
        label: "average_ticket_per_diner",
        value: "avg_customer_ticket.gross"
      }
    ],
    forecast_items: {
      month: [
        {
          label: "monthly_projection",
          value: "forecast_month.gross"
        },
        {
          label: "person_cost_ratio",
          value: "revenuerestaurant_forecast_staff_cost_month_avg"
        },
        {
          label: "rent_cost_ratio",
          value: "revenuerestaurant_forecast_rentings_cost_month_avg"
        },
        {
          label: "purchasing_ratio",
          value: "revenuerestaurant_forecast_other_cost_month_avg"
        },
        {
          label: "other_expenses_ratio",
          value: "revenuerestaurant_forecast_sales_cost_month_avg"
        },
        {
          label: "tax_percent_iva",
          value: "tax_percent"
        },
        {
          label: "tax_iva",
          value: "tax"
        },
        {
          label: "sale_result",
          value: "sale_result"
        }
      ],
      year: [
        {
          label: "yearly_projection",
          value: "forecast_year.gross"
        },
        {
          label: "person_cost_ratio",
          value: "revenuerestaurant_forecast_staff_cost_year_avg"
        },
        {
          label: "rent_cost_ratio",
          value: "revenuerestaurant_forecast_rentings_cost_year_avg"
        },
        {
          label: "purchasing_ratio",
          value: "revenuerestaurant_forecast_other_cost_year_avg"
        },
        {
          label: "other_expenses_ratio",
          value: "revenuerestaurant_forecast_sales_year_avg"
        },
        {
          label: "tax_percent_iva",
          value: "tax_percent"
        },
        {
          label: "tax_iva",
          value: "tax"
        },
        {
          label: "sale_result",
          value: "sale_result"
        }
      ]
    }
  }),
  computed: {
    sales_report_list_labels: {
      get() {
        return this.$store.state.restaurant.sales_report_list_labels;
      },
      set(value) {
        this.$store.dispatch("setSalesReportListLabels", value);
      }
    },
    // Fecha de ayer formateada
    yesterdayDate() {
      return Vue.moment()
        .subtract(1, "days")
        .format("DD/MM/YYYY");
    },
    periodVs() {
      if (this.sales_vs === "presupuesto") {
        return "bud";
      } else if (this.sales_vs === "ano_pasado") {
        return "ly";
      }
      return "otb";
    },
    mode() {
      if (this.sales_period === "current_month") {
        return "month";
      } else if (this.sales_period === "current_year") {
        return "year";
      } else if (
        this.sales_period === "past" &&
        this.sales_period_month !== null &&
        this.sales_period_year !== null
      ) {
        return "month";
      } else if (
        this.sales_period === "past" &&
        this.sales_period_month === null &&
        this.sales_period_year !== null
      ) {
        return "year";
      }
      return "month";
    },
    // Rango de fechas para el período seleccionado
    periodDateRange() {
      let from_report = Vue.moment(this.sales_report_start_date).format(
        "DD-MM-YYYY"
      );
      let to_report = Vue.moment(this.sales_report_end_date).format(
        "DD-MM-YYYY"
      );
      let from_compare = Vue.moment(
        this.sales_report_start_date_to_compare
      ).format("DD-MM-YYYY");
      let to_compare = Vue.moment(this.sales_report_end_date_to_compare).format(
        "DD-MM-YYYY"
      );
      let vsText = this.$t("against");
      if (this.sales_vs === "presupuesto") {
        vsText += " " + this.$t("bud");
      } else if (this.sales_vs === "ano_pasado") {
        vsText += " " + this.$t("ly");
      } else if (this.sales_vs === "otro_hotel") {
        let hotelData = this.$store.state.hotel.hotels.find(
          h => h.id === this.sales_hotel_vs
        );
        vsText += " " + hotelData.name;
      }

      let text = "";
      if (from_report && to_report) {
        text =
          this.$t("from") +
          " " +
          from_report +
          " " +
          this.$t("to") +
          " " +
          to_report +
          " " +
          vsText;
      }
      if (from_compare && to_compare) {
        text +=
          " " +
          this.$t("from_minus") +
          " " +
          from_compare +
          " " +
          this.$t("to") +
          " " +
          to_compare;
      }
      return text;
    },
    // Datos formateados para el gráfico
    chartData() {
      let result = { period: [], vs: [] };

      // Verificar si existen los datos necesarios
      if (
        !this.sales_report_list ||
        !this.sales_report_list.daily_list ||
        !this.sales_report_list.daily_list_to_compare
      ) {
        return result;
      }

      // Extraer datos del periodo actual
      result.period = this.sales_report_list.daily_list.map(item => {
        return item.total_sale && item.total_sale.gross
          ? item.total_sale.gross
          : 0;
      });

      // Extraer datos del periodo de comparación
      result.vs = this.sales_report_list.daily_list_to_compare.map(item => {
        return item.total_sale && item.total_sale.gross
          ? item.total_sale.gross
          : 0;
      });

      return result;
    },
    chartLabels() {
      return this.getChartLabels();
    },
    getKpiPeriodsCard() {
      let periods = ["otb"];
      if (this.sales_vs === "presupuesto") {
        periods.push("bud");
      } else if (this.sales_vs === "ano_pasado") {
        periods.push("ly");
      } else if (this.sales_vs === "otro_hotel") {
        periods.push("otb");
      }
      return periods;
    },
    getKpiPeriods() {
      let periods = [];
      if (this.sales_vs === "presupuesto") {
        periods.push("bud");
      } else if (this.sales_vs === "ano_pasado") {
        periods.push("ly");
      } else if (this.sales_vs === "otro_hotel") {
        periods.push("otb");
      }
      return periods;
    },
    ...mapState({
      sales_period: state => state.restaurant.sales_period,
      sales_period_month: state => state.restaurant.sales_period_month,
      sales_period_year: state => state.restaurant.sales_period_year,
      sales_vs: state => state.restaurant.sales_vs,
      sales_hotel_vs: state => state.restaurant.sales_hotel_vs,
      sales_report_start_date: state =>
        state.restaurant.sales_report_start_date,
      sales_report_end_date: state => state.restaurant.sales_report_end_date,
      sales_report_start_date_to_compare: state =>
        state.restaurant.sales_report_start_date_to_compare,
      sales_report_end_date_to_compare: state =>
        state.restaurant.sales_report_end_date_to_compare,
      sales_report_data: state => state.restaurant.sales_report_data,
      sales_report_list: state => state.restaurant.sales_report_list
    })
  },
  methods: {
    // Obtiene el kpi correcto de los datos de ayer
    getKpi(path) {
      if (!path) return null;
      const parts = path.split(".");
      const firstPart = parts[0];
      return firstPart;
    },
    getValue(path, source) {
      if (!path || !source) return null;
      // Divide la ruta en partes (para manejar las propiedades anidadas)
      const parts = path.split(".");
      // Comienza con el objeto raíz
      let result = this.sales_report_data[source];
      // Navega por cada parte de la ruta
      for (const part of parts) {
        if (!result || result[part] === undefined) {
          return null;
        }
        result = result[part];
      }
      return parseFloat(result);
    },
    getChartLabels() {
      const labels = this.sales_report_list.daily_list.map(item => {
        return item.date_original;
      });
      return labels;
    },
    // Obtiene la etiqueta para el período actual
    getPeriodLabel() {
      let monthName = "";

      switch (this.sales_period) {
        case "current_month":
          return Vue.moment().format("MMMM YYYY");
        case "current_year":
          return Vue.moment().format("YYYY");
        case "past":
          if (!this.sales_period_month || !this.sales_period_year)
            return this.$t("select");
          monthName = Vue.moment()
            .month(this.sales_period_month - 1)
            .format("MMMM");
          return `${monthName} ${this.sales_period_year}`;
        default:
          return this.$t("select");
      }
    },
    // Obtiene la etiqueta para la comparación
    getCompareLabel() {
      switch (this.sales_vs) {
        case "presupuesto":
          return this.$t("bud");
        case "ano_pasado":
          return this.$t("ly");
        case "otro_hotel": {
          const hotelData = this.$store.state.hotel.hotels.find(
            h => h.id === this.sales_hotel_vs
          );
          return hotelData ? hotelData.name : this.$t("Other_restaurant");
        }
        default:
          return this.$t("select");
      }
    }
  }
};
</script>

<style lang="scss">
.a-restaurant-sales-resume {
  .a-card-kpi-icon {
    .subtitle-2 {
      font-size: 0.75rem !important;
    }
    .kpi-wrapper-2.large {
      margin-bottom: 0px !important;
    }
  }
}
@media (max-width: 1200px) {
  .a-restaurant-sales-resume {
    .grid-forecast {
      flex-wrap: wrap;
      .col {
        flex-basis: 25% !important;
      }
    }
  }
}
@media (max-width: 800px) {
  .a-restaurant-sales-resume {
    .grid-forecast {
      .col {
        flex-basis: 50% !important;
      }
    }
  }
}
@media (max-width: 600px) {
  .a-restaurant-sales-resume {
    .grid-forecast {
      .col {
        flex-basis: 100% !important;
      }
    }
    .production-title {
      flex-direction: column;
    }
  }
}
</style>
